import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart3, Filter, AlertTriangle, Users, TrendingUp, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { coberturaAPI } from '../services/api';
import HeaderOrdenavel, { alternarOrdem, compararValores } from '../components/HeaderOrdenavel';
import SelecaoMultipla from '../components/SelecaoMultipla';
import { montarCsv, baixarCsv, stampData } from '../utils/csv';

/**
 * Página /cobertura
 * KPI Taxa de Cobertura em destaque, tabela por segmento, filtros de cidade e optin.
 * Filtro de optin LIGADO por padrão (D12): 76% da base não tem optin,
 * disparar para não-optados é exposição LGPD.
 */
const Cobertura = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const clienteId = user?.cliente_id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [porSegmento, setPorSegmento] = useState([]);
  const [participantesUnicos, setParticipantesUnicos] = useState(0);

  // Filtros — optin ligado por padrão (D12)
  const [filtroOptin, setFiltroOptin] = useState(true);
  const [filtroCidades, setFiltroCidades] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [exportando, setExportando] = useState(false);

  // Dados sem filtro de optin para mostrar os dois números
  const [kpiSemOptin, setKpiSemOptin] = useState(null);

  // Ordenação da tabela — no cliente, são 13 linhas
  const [ordem, setOrdem] = useState({ campo: 'total', dir: 'desc' });

  // Ferramentas de exportação só com ?export=true (padrão de Grupos do WhatsApp)
  const [searchParams] = useSearchParams();
  const showExport = searchParams.get('export') === 'true';

  const ordenarPor = (campo) => {
    // Colunas numéricas começam em desc: o interesse é o maior valor
    const dirInicial = campo === 'segmento_nome' ? 'asc' : 'desc';
    setOrdem((atual) => alternarOrdem(atual, campo, dirInicial));
  };

  // A taxa é derivada, então é calculada antes de ordenar
  const segmentosOrdenados = React.useMemo(() => {
    const comTaxa = porSegmento.map((s) => ({
      ...s,
      taxa: s.total > 0 ? Math.round((s.ja_em_grupo / s.total) * 100) : 0,
    }));
    return comTaxa.sort((a, b) => compararValores(a, b, ordem.campo, ordem.dir));
  }, [porSegmento, ordem]);

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const filtros = {};
      if (filtroOptin) filtros.optin = true;
      if (filtroCidades.length) filtros.cidades = filtroCidades;

      const res = await coberturaAPI.getCobertura(clienteId, filtros);
      setKpi(res.data.kpi);
      setPorSegmento(res.data.por_segmento || []);
      setParticipantesUnicos(res.data.participantes_unicos_grupos || 0);

      // Cidades vêm do backend: por_segmento é agregado e não carrega cidade
      setCidades(res.data.cidades || []);

      // Se optin está ligado, buscar também sem optin para comparação
      if (filtroOptin) {
        try {
          const filtrosSem = { ...filtros };
          delete filtrosSem.optin;
          const resSem = await coberturaAPI.getCobertura(clienteId, filtrosSem);
          setKpiSemOptin(resSem.data.kpi);
        } catch { setKpiSemOptin(null); }
      } else {
        setKpiSemOptin(null);
      }
    } catch (error) {
      console.error('Erro ao carregar cobertura:', error);
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar dados de cobertura');
      }
    } finally {
      setLoading(false);
    }
  }, [clienteId, filtroOptin, filtroCidades]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Exporta a tabela por segmento. Gerado no cliente porque são dados
   * agregados (contagens), sem dado pessoal, e assim o arquivo sai exatamente
   * como está na tela — mesma ordenação e mesmos filtros.
   */
  const exportarResumo = () => {
    const colunas = [
      { chave: 'segmento_nome', titulo: 'Segmento' },
      { chave: 'segmento_alias', titulo: 'Alias' },
      { chave: 'total', titulo: 'Total' },
      { chave: 'ja_em_grupo', titulo: 'Ja em grupo' },
      { chave: 'fora_grupo', titulo: 'Fora de grupo' },
      { chave: 'com_optin', titulo: 'Fora e com optin' },
      { chave: 'taxa', titulo: 'Taxa de cobertura (%)' },
    ];
    const sufixo = [
      filtroOptin ? 'optin' : null,
      filtroCidades.length === 1 ? filtroCidades[0].replace(/\s+/g, '-') : (filtroCidades.length ? `${filtroCidades.length}cidades` : null),
    ].filter(Boolean).join('-');

    baixarCsv(
      `cobertura-resumo${sufixo ? '-' + sufixo : ''}-${stampData()}`,
      montarCsv(colunas, segmentosOrdenados),
    );
    toast.success(`Resumo exportado (${segmentosOrdenados.length} segmentos)`);
  };

  // Export CSV com auditoria no backend
  const handleExportar = async () => {
    setExportando(true);
    try {
      const filtros = {};
      if (filtroOptin) filtros.optin = true;
      if (filtroCidades.length) filtros.cidades = filtroCidades;
      // Sem em_grupo explícito o backend exporta quem está FORA de grupo,
      // que é o público de remarketing

      const res = await coberturaAPI.exportar(clienteId, filtros);
      // O backend já devolve o CSV pronto e registra a exportação para auditoria
      baixarCsv(`cobertura-fora-de-grupo-${stampData()}`, res.data);
      toast.success('CSV exportado');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    } finally {
      setExportando(false);
    }
  };

  const formatNumber = (n) => {
    if (n === null || n === undefined) return '—';
    return n.toLocaleString('pt-BR');
  };

  const formatPercent = (n) => {
    if (n === null || n === undefined) return '—';
    return `${Math.round(n)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title="Cobertura" subtitle="Análise de presença da base em grupos WhatsApp" />
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-nomoz-orange"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Cobertura" subtitle="Análise de presença da base em grupos WhatsApp" />

        {/* Ferramentas de exportação — só com ?export=true, para não poluir o layout
            (mesmo padrão de Grupos do WhatsApp) */}
        {showExport && (
          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={exportarResumo}
              disabled={segmentosOrdenados.length === 0}
              title="Exporta a tabela por segmento como está na tela, respeitando ordenação e filtros"
              className={`text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 ${
                darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              📥 Exportar resumo por segmento (CSV)
            </button>
            <button
              onClick={handleExportar}
              disabled={exportando}
              title="Exporta os contatos que estão fora dos grupos, respeitando os filtros"
              className={`text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 ${
                darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              📥 {exportando ? 'Exportando...' : 'Exportar contatos fora de grupo (CSV)'}
            </button>
          </div>
        )}

        {/* KPI Principal em destaque */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Taxa de Cobertura — KPI central */}
          <div className={`md:col-span-2 rounded-lg p-6 border-2 ${
            darkMode ? 'bg-zinc-800 border-nomoz-orange/50' : 'bg-white border-nomoz-orange/30'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="text-nomoz-orange" size={24} />
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Taxa de Cobertura {filtroOptin && '(apenas optados)'}
              </p>
            </div>
            <p className={`text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatPercent(kpi?.taxa_cobertura)}
            </p>
            <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatNumber(kpi?.presentes_grupo)} de {formatNumber(kpi?.total_base)} contatos presentes em grupos
            </p>
          </div>

          {/* Contatos na base */}
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Base total</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatNumber(kpi?.total_base)}
                </p>
              </div>
              <Users className={`h-8 w-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
          </div>

          {/* Fora de grupo */}
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fora de grupo</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatNumber(kpi?.fora_grupo)}
                </p>
              </div>
              <BarChart3 className={`h-8 w-8 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            </div>
          </div>
        </div>

        {/* Aviso LGPD e números sem optin */}
        {filtroOptin && kpiSemOptin && (
          <div className={`rounded-lg p-4 border ${
            darkMode ? 'bg-yellow-900/10 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className={`text-sm font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  Filtro de Optin ativo — dados acima consideram apenas contatos com consentimento
                </p>
                <p className={`text-xs mt-1 ${darkMode ? 'text-yellow-400/80' : 'text-yellow-700'}`}>
                  Base completa (sem filtro optin): {formatNumber(kpiSemOptin.total_base)} contatos, {formatNumber(kpiSemOptin.fora_grupo)} fora de grupo.
                  <span className="font-semibold"> ⚠️ Enviar para {formatNumber((kpiSemOptin.total_base || 0) - (kpi?.total_base || 0))} contatos sem optin expõe a LGPD.</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Card participantes em grupos */}
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <Shield className={darkMode ? 'text-green-400' : 'text-green-600'} size={20} />
            <div>
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Participantes únicos nos grupos WhatsApp: 
              </span>
              <span className={`font-bold ml-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatNumber(participantesUnicos)}
              </span>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <Filter size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />

            {/* Filtro Optin — toggle (ligado por padrão, D12) */}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Apenas optados:</span>
              <button
                onClick={() => setFiltroOptin(!filtroOptin)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  filtroOptin ? 'bg-green-500' : darkMode ? 'bg-zinc-600' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  filtroOptin ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Filtro Cidade — múltiplas, por clique. Digitar é fonte de erro:
                a base tem grafias diferentes e o usuário não sabe o que existe */}
            <SelecaoMultipla
              opcoes={cidades}
              valores={filtroCidades}
              onChange={setFiltroCidades}
              placeholder="Todas as cidades"
              titulo="Filtrar por uma ou mais cidades"
              darkMode={darkMode}
              larguraMinima={190}
            />
          </div>
        </div>

        {/* Tabela por Segmento */}
        <div className={`rounded-lg overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`px-6 py-4 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Cobertura por Segmento
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
                  <HeaderOrdenavel campo="segmento_nome" ordem={ordem} onOrdenar={ordenarPor} darkMode={darkMode}>
                    Segmento
                  </HeaderOrdenavel>
                  <HeaderOrdenavel campo="total" ordem={ordem} onOrdenar={ordenarPor} alinhar="right" darkMode={darkMode}>
                    Total
                  </HeaderOrdenavel>
                  <HeaderOrdenavel campo="ja_em_grupo" ordem={ordem} onOrdenar={ordenarPor} alinhar="right" darkMode={darkMode}>
                    Já em grupo
                  </HeaderOrdenavel>
                  <HeaderOrdenavel campo="fora_grupo" ordem={ordem} onOrdenar={ordenarPor} alinhar="right" darkMode={darkMode}>
                    Fora
                  </HeaderOrdenavel>
                  <HeaderOrdenavel campo="com_optin" ordem={ordem} onOrdenar={ordenarPor} alinhar="right" darkMode={darkMode}>
                    Com Optin
                  </HeaderOrdenavel>
                  <HeaderOrdenavel campo="taxa" ordem={ordem} onOrdenar={ordenarPor} alinhar="right" darkMode={darkMode}
                    title="Ordenar pela taxa de cobertura">
                    Taxa
                  </HeaderOrdenavel>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {porSegmento.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`px-6 py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Nenhum dado de cobertura disponível. Configure uma integração de audiência e sincronize os dados.
                    </td>
                  </tr>
                ) : segmentosOrdenados.map((seg, idx) => {
                  const taxa = seg.taxa;
                  return (
                    <tr
                      key={seg.segmento_id || idx}
                      onClick={() => seg.segmento_id && navigate(`/cobertura/segmento/${seg.segmento_id}`)}
                      title="Ver contatos deste segmento"
                      className={`${darkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-white hover:bg-gray-50'} ${
                        seg.segmento_id ? 'cursor-pointer' : ''
                      }`}
                    >
                      <td className={`px-6 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {seg.segmento_nome || seg.segmento_alias || `Segmento ${idx + 1}`}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatNumber(seg.total)}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {formatNumber(seg.ja_em_grupo)}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {formatNumber(seg.fora_grupo)}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatNumber(seg.com_optin)}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right font-medium`}>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          taxa >= 50
                            ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                            : taxa >= 20
                              ? darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                              : darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
                        }`}>
                          {taxa}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Cobertura;
